import { loadMaps } from './maps';

/**
 * Address autocomplete, on top of the Maps script loaded by `maps.ts`. Like the
 * map itself it is best-effort: no key, no Places API, an offline device — the
 * caller simply gets no suggestion and the plain input keeps working.
 */

/** Her whole tour is around Toulouse; the radius is a bias, never a filter. */
const TOULOUSE = { lat: 43.6045, lng: 1.4442 };
const BIAS_RADIUS_M = 40_000;
const MAX_SUGGESTIONS = 5;

let libraryPromise: Promise<google.maps.PlacesLibrary | null> | null = null;
/**
 * A rejection can be the road (she types offline) or the API being off for good.
 * Retry a couple of times, then give up for the session rather than ask forever.
 */
const MAX_FAILURES = 3;
let failures = 0;

async function loadPlaces(): Promise<google.maps.PlacesLibrary | null> {
  libraryPromise ??= importPlaces();
  return libraryPromise;
}

async function importPlaces(): Promise<google.maps.PlacesLibrary | null> {
  const maps = await loadMaps();
  if (!maps?.importLibrary) return null;
  try {
    const places = await maps.importLibrary('places');
    return places.AutocompleteSuggestion ? places : null;
  } catch {
    return null;
  }
}

export interface AddressSuggestion {
  /** What gets stored: the address as Google spells it. */
  address: string;
  /** Split for display only — the street, then the town. */
  main: string;
  secondary: string;
}

export interface AddressSession {
  suggest: (input: string) => Promise<AddressSuggestion[]>;
  /** Ends the billing session — call it once she has picked an address. */
  reset: () => void;
}

/**
 * A typing session: every request she makes while composing one address shares a
 * token, which is what Google bills as a single autocomplete session.
 */
export function createAddressSession(): AddressSession {
  let token: google.maps.places.AutocompleteSessionToken | null = null;

  return {
    async suggest(input) {
      const places = await loadPlaces();
      if (!places || failures >= MAX_FAILURES) return [];

      token ??= new places.AutocompleteSessionToken();
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: token,
          language: 'fr',
          region: 'FR',
          includedRegionCodes: ['fr'],
          locationBias: { center: TOULOUSE, radius: BIAS_RADIUS_M },
        });
        failures = 0;
        return suggestions.flatMap(toAddress).slice(0, MAX_SUGGESTIONS);
      } catch {
        failures += 1;
        return [];
      }
    },

    reset() {
      token = null;
    },
  };
}

function toAddress(suggestion: google.maps.places.AutocompleteSuggestion): AddressSuggestion[] {
  const prediction = suggestion.placePrediction;
  if (!prediction) return [];
  const address = prediction.text.toString();
  if (!address) return [];
  return [
    {
      address,
      main: prediction.mainText?.toString() ?? address,
      secondary: prediction.secondaryText?.toString() ?? '',
    },
  ];
}
